import { useCallback, useEffect, useRef, useState } from "react";
import notificationSound from "../audio/notification_clock_alarm.mp3";

export const useLoopingNotification = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isUnlocked, setUnlocked] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    // Create audio instance
    audioRef.current = new Audio(notificationSound);
    audioRef.current.loop = true;

    // Unlock audio on first user interaction
    const unlock = () => {
      audioRef.current?.play().then(() => {
        audioRef.current?.pause();
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
        }
        setUnlocked(true);
        window.removeEventListener("click", unlock);
      }).catch(() => {
        // Ignore errors during unlock
      });
    };

    window.addEventListener("click", unlock, { once: true });

    return () => {
      window.removeEventListener("click", unlock);
      // Cleanup audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      setIsPlaying(false);
    };
  }, []);

  const play = useCallback(() => {
    if (!audioRef.current) return;

    if (audioRef.current.paused) {
      audioRef.current.currentTime = 0;
      audioRef.current.volume = 1;
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch((error) => {
        console.warn("Failed to play notification sound:", error);
        // Audio playback failed (likely autoplay blocked)
        setIsPlaying(false);
      });
    }
  }, [isUnlocked]);

  const stop = useCallback(() => {
    if (!audioRef.current) return;

    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
  }, []);

  return { play, stop, isPlaying, isUnlocked };
};
